import { api, errorText } from "../api";
import { copyToClipboard } from "../clipboard";
import { toast } from "../toast";
import type { KeyStatus } from "../types";

/* The three things that have to be true before a new instance works, in the
   order they have to happen. A freshly claimed panel used to be an empty feed
   and four admin tabs, which is a puzzle rather than an onboarding: the overlay
   URL in particular is one click deep and easy to never find.

   Every step is derived from live state — nothing is stored, nothing is marked
   done by hand — so the card is honest about what is actually set up, and
   disappears on its own once it is. */

interface Props {
  keyStatus: KeyStatus | null;
  overlaysConnected: number;
  hasSpoken: boolean;
  overlayUrl: string | null;
  onOpenSettings: () => void;
  /* Put a sample line in the composer and focus it. */
  onTestLine: () => void;
  onDismiss: () => void;
}

interface Step {
  done: boolean;
  title: string;
  body: string;
  /* Every step that can be done from here has its button here: a checklist
     that only says what to do sends somebody off to find it. */
  actions?: { label: string; run: () => void }[];
}

export function FirstRun({
  keyStatus,
  overlaysConnected,
  hasSpoken,
  overlayUrl,
  onOpenSettings,
  onTestLine,
  onDismiss,
}: Props) {
  const steps: Step[] = [
    {
      done: Boolean(keyStatus?.set),
      title: "Add your ElevenLabs API key",
      body: "Required to generate speech.",
      actions: [{ label: "Open settings", run: onOpenSettings }],
    },
    {
      done: overlaysConnected > 0,
      title: "Add the overlay to OBS",
      body: "Add it as a Browser source and enable “Control audio via OBS”.",
      actions: overlayUrl
        ? [
            {
              label: "Copy overlay URL",
              run: () => void copyToClipboard(overlayUrl, "Overlay URL copied"),
            },
          ]
        : undefined,
    },
    {
      done: hasSpoken,
      title: "Send a test message",
      body: "You should hear it in OBS.",
      actions: [
        { label: "Write a test line", run: onTestLine },
        ...(overlaysConnected > 0
          ? [
              {
                label: "Play test tone",
                run: () =>
                  void api("/api/admin/overlay/test", { method: "POST" })
                    .then(() => toast("Test tone sent"))
                    .catch((error) => toast(errorText(error), "error")),
              },
            ]
          : []),
      ],
    },
  ];

  const remaining = steps.filter((step) => !step.done).length;
  if (remaining === 0) return null;

  return (
    <div className="card first-run">
      <h2>
        Set up your instance <span className="spacer"></span>
        <span className="muted">
          {steps.length - remaining} of {steps.length} done
        </span>
      </h2>

      <ol className="checklist">
        {steps.map((step) => (
          <li key={step.title} className={step.done ? "done" : ""}>
            <span className="tick" aria-hidden="true">
              {step.done ? "✓" : ""}
            </span>
            <div className="checklist-text">
              <div className="checklist-title">{step.title}</div>
              <div className="hint">{step.body}</div>
              {!step.done && step.actions && (
                <div className="checklist-actions">
                  {step.actions.map((action) => (
                    <button key={action.label} className="ghost" onClick={action.run}>
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      <button className="link" onClick={onDismiss}>
        Hide this
      </button>
    </div>
  );
}
